import { addDays, differenceInDays, differenceInHours, differenceInMinutes, isAfter, format, getUnixTime } from 'date-fns';

export function processInspectionsData(obj) {
    return obj.inspections.map((row) => {
        return {
            createdAt: format(new Date(row.createdAt), 'dd/MM/yy, hh:mmaaa'),
            uuid: row.uuid,
            site_id: row.site_id,
            inspection_type: row.inspection_type.replace("_", " "),
            inspection_result: row.inspection_result,
            notes: row.notes,
            images: row.images.urls
        };
    })
}

export function getInspectionIconColour(status) {
    switch (status) {
        case 'inspected':
            return { color: 'info', icon: 'search', hex: "#17c1e8" };
        case 'review':
            return { color: 'warning', icon: 'search', hex: "#fbcf33" };
        case 'unresolved':
            return { color: 'error', icon: 'notifications', hex: "#ea0606" };
        case 'resolved':
            return { color: 'success', icon: 'verified_check', hex: "#82d616" };
        default:
            return { color: 'secondary', icon: 'question_mark', hex: "#8392ab" };
    }
}

export function getStatusColour(status) {
    if (status === 'review') return "warning";
    if (status === 'inspected') return "info";
    if (status === "unresolved") return "error";
    if (status === "resolved") return "success";
    return "secondary";
}

export function calculateOverdue(earliestTime, selectedOrg, colour = false) {
    let overdue_days = 0;

    switch (selectedOrg) {
        case 'yarra-trams':
            overdue_days = 1;
            break;
        default:
            overdue_days = 7;
            break;
    }

    const current = new Date();
    const due = addDays(new Date(earliestTime * 1000), overdue_days);

    // is overdue if current time is past the due date
    const colorBadge = current > due ? "255, 0, 0, 0.3" : "0, 0, 0, 0.3";

    if (colour) return colorBadge;
    return current > due;
}

export function calculateTimeDiff(dueTime) {
    const current = new Date();
    const due = new Date(dueTime * 1000);

    // Use absolute difference so the math works perfectly for both past and future
    const totalDays = Math.abs(differenceInDays(due, current));
    let timeString = "";

    // Handle less than 1 day
    if (totalDays < 1) {
        const hours = Math.abs(differenceInHours(due, current));

        // Fallback to minutes if less than an hour
        if (hours < 1) {
            const minutes = Math.abs(differenceInMinutes(due, current));
            if (minutes < 1) {
                return "Now";
            }
            timeString = minutes === 1 ? `1 min` : `${minutes} mins`;
        } else {
            timeString = hours === 1 ? `1 hour` : `${hours} hours`;
        }
    }
    // Handle less than a week
    else if (totalDays < 7) {
        timeString = totalDays === 1 ? `1 day` : `${totalDays} days`;
    }
    // Handle 7 or more days (weeks + remaining days)
    else {
        const weeks = Math.floor(totalDays / 7);
        const remainingDays = totalDays % 7;

        const weekStr = weeks === 1 ? '1 week' : `${weeks} weeks`;
        const dayStr = remainingDays === 1 ? '1 day' : `${remainingDays} days`;

        timeString = remainingDays === 0 ? weekStr : `${weekStr}, ${dayStr}`;
    }

    // Format the final label based on whether it is overdue or upcoming
    return isAfter(current, due) ? `${timeString} ago` : `${timeString}`;
}

export function determineInspectionStatus(inspection_type, inspection_data) {
    var overallStatus = "inspected";
    var requiresReview = false;

    for (var dev_index in inspection_data.devices) {
        const device = inspection_data.devices[dev_index];

        let deviceStatus = "resolved";

        // 1. Get an array of the active condition keys (e.g., ['power', 'vandalised'])
        let activeConditionKeys = Object.entries(device.conditions).filter(([key, val]) => val === true || (key === 'other' && typeof val === 'string' && val.length > 0)).map(([key]) => key);

        const hasActiveConditions = activeConditionKeys.length > 0;
        const activeActions = Object.keys(device.actions).filter(key => device.actions[key] === true);

        if (!hasActiveConditions && activeActions.length === 0) {
            deviceStatus = "inspected";
        }
        else if (hasActiveConditions) {
            deviceStatus = "unresolved";

            if (activeActions.length === 0) {
                return "unresolved";
            }

            // 2. Iterate through actions and remove the corresponding conditions from the array
            for (const action of activeActions) {
                switch (action) {
                    case 'removed':
                        // Removes multiple possible conditions
                        activeConditionKeys = activeConditionKeys.filter(c => !['location', 'frozen', 'damaged'].includes(c));
                        break;
                    case 'installed':
                        // Add any condition keys here if 'installed' resolves something specific
                        requiresReview = true;
                        break;
                    case 'cleaned':
                        activeConditionKeys = activeConditionKeys.filter(c => c !== 'vandalised');
                        break;
                    case 'battery_swap':
                        activeConditionKeys = activeConditionKeys.filter(c => c !== 'power');
                        break;
                    case 'hardware_changed':
                        activeConditionKeys = activeConditionKeys.filter(c => c !== 'audio');
                        break;
                    case 'cosmetically_fixed':
                        activeConditionKeys = activeConditionKeys.filter(c => !['vandalised', 'damaged'].includes(c));
                        break;
                    case 'other':
                        activeConditionKeys = activeConditionKeys.filter(c => c !== 'other');
                        break;
                }
            }

            // 3. If the array is empty, all conditions were successfully covered by actions
            if (activeConditionKeys.length === 0) {
                deviceStatus = inspection_type === "inspection" ? "resolved" : "review";
            }
        }

        if (deviceStatus === "unresolved") {
            return "unresolved";
        }
        if (deviceStatus === "review") {
            requiresReview = true;
        }
        if (deviceStatus === "resolved" && overallStatus !== "review") {
            overallStatus = "resolved";
        }
    }

    return requiresReview ? "review" : overallStatus;
}

export function filterOptions(option, searchText) {
    const { value } = option;
    const search = searchText.toLowerCase();

    return (
        typeof value?.name != null && value?.name != "" && value?.name?.toLowerCase().includes(search) ||
        typeof value?.serial_number != null && value?.serial_number != "" && value?.serial_number?.toLowerCase().includes(search) ||
        typeof value?.stop_id != null && value?.stop_id != "" && value?.stop_id?.toLowerCase().includes(search) ||
        typeof value?.uuid != null && value?.uuid != "" && value?.uuid?.toLowerCase().includes(search) ||
        typeof value?.site_id != null && value?.site_id != "" && value?.site_id?.toLowerCase().includes(search)
    )
}

export function calcDistanceSuffix(device_distance) {
    if (device_distance) {
        var method = "m";
        var distance_away = device_distance;

        if (distance_away > 100) {
            distance_away /= 1000;
            method = "km";
        };

        return ({ type: "~", amount: `${distance_away.toFixed(2)}`, method });
    }

    return {};
}

export function getArrValues(values, deviceLabel, method, create_object = false) {
    var ret = [];
    var method_values = Object.keys(values).filter((row) => row.includes(`${deviceLabel}-${method}`));

    for (var el of method_values) {
        var element_arr = el.split('-');
        var type = element_arr[(element_arr.length - 1)];

        if (values[`${deviceLabel}-${method}-${type}`] == true || (type == "other" && values[`${deviceLabel}-${method}-${type}`].length > 0)) {
            if (!create_object) {
                ret.push(values[`${deviceLabel}-${method}-${type}`]);
            } else {
                ret.push([type, values[`${deviceLabel}-${method}-${type}`]]);
            }
        }
    }

    return ret;
}

export function determineReportingDate(item, selectedOrg) {
    var label = "Reported: ";
    var reported_date = null;
    var timer_date = null;
    let overdue = false;
    let tag = { label: null, color: "0, 0, 0, 0.3" };
    let timescale = "";

    const completion = (item.status == 'resolved' || item.status == 'inspected') && item.inspectionData?.descendant_tree?.length == 0;

    // Current date: 20 Aug
    // If reported date is in the past
    // Don't show due
    // remove scheduled tag and icon
    // set overdue timer from past date

    // If reported date is in the future
    // Show due
    // use scheduled tag and icon
    // set overdue time from created date

    // Otherwise normal logic

    var recordedDate = getUnixTime(new Date(item.inspectionData.recordedAt));
    var createdDate = getUnixTime(new Date(item.inspectionData.createdAt));

    const out_of_range = 10 * 60;

    if (Math.abs(recordedDate - createdDate) >= out_of_range) { // not a current inspection, 5mins from creation

        if (recordedDate > createdDate) { // in the future
            timescale = "future";
            label = "Scheduled: ";
            reported_date = recordedDate;
            timer_date = recordedDate;
            tag = { label: "scheduled", color: "secondary" };

        }
        else { // in the past
            timescale = "past";
            label = "Reported: ";
            reported_date = recordedDate;
            timer_date = recordedDate;
            tag = { label: item.status, color: getStatusColour(item.status) };
        }
    }
    else {
        timescale = "current";
        label = "Reported: ";
        reported_date = createdDate;
        timer_date = createdDate;
        tag = { label: item.status, color: getStatusColour(item.status) };
    }

    if (completion) {
        label = "Completed: ";
    }

    if (item.status != "resolved" && item.status != "inspected" && item.inspectionData?.descendant_tree?.length == 0) {
        overdue = calculateOverdue(timer_date, selectedOrg);
        if (overdue) {
            tag = { label: 'overdue', color: 'error' };
        }
    }

    return { timescale, label, reported_date, timer_date, tag, overdue };
}

export const conditionConfig = {
    "vandalised": { color: "primary", icon: "SprayCan" },
    "damaged": { color: "secondary", icon: "BrokenDevice" },
    "audio": { color: "info", icon: "volume_off" },
    "power": { color: "brown", icon: "battery_charging_full" },
    "frozen": { color: "warning", icon: "app_blocking" },
    "location": { color: "error", icon: "fmd_bad" },
    "other": { color: "dark", icon: "more_horiz" }
};

export const conditionOptions = Object.keys(conditionConfig);

export const actionOptions = [
    "removed", "installed", "cleaned", "battery swap", "hardware changed", "cosmetically fixed", "other"
];

export const visitTypeOptions = [
    { value: "callout", label: "Callout" },
    { value: "routine", label: "Routine Maintenance" },
    { value: "both", label: "All Visits" }
];

// Helper to get YYYY-MM-DD format
export const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// Helper to get default last month date range
export const getLastMonthRange = () => {
    const now = new Date();
    const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    return {
        start: formatDate(firstDayLastMonth),
        end: formatDate(lastDayLastMonth)
    };
};